import { fabric } from 'fabric';
import { ANNOTATION_TYPE } from '@/shared/ui';

type EventIndicatorCtor = new (
  cv: fabric.Canvas,
  sampleIndex: number,
  offsetX: number,
  config: any,
  text: string,
) => any;

let EventIndicatorImpl: any;

(function () {
  const STROKE = {
    strokeWidth: 1,
    selectable: false,
    hasBorders: false,
    stroke: '#EB6D17',
    originX: 'center',
    originY: 'center',
    hoverCursor: 'arrow',
  };

  EventIndicatorImpl = function (
    cv: fabric.Canvas,
    sampleIndex: number,
    offsetX: number,
    config: any,
    text: string,
  ) {
    this.type = ANNOTATION_TYPE.EVENT_INDICATOR;
    this.text = text;
    this.canvas = cv;
    this.config = config;
    this.isVisible = true;
    this.xr = sampleIndex;
    this.spp = (config.samplingRate * config.wndDuration) / config.width;
    this.x = this.xr > offsetX ? (this.xr - offsetX) / this.spp : this.xr / this.spp;
    this.squareSize = 18.9; // config.majorDivSize of speed 25 / 5 grid;
  };

  EventIndicatorImpl.prototype.draw = function (sampleIndex: number, offsetX: number) {
    this.xr = sampleIndex;
    this.x = (this.xr - offsetX) / this.spp;
    let lineX = this.x;
    let leftRectWithText = this.x - this.squareSize;
    if (this.x < this.squareSize) {
      // near left edge
      leftRectWithText = 0;
      if (this.x < 0) {
        lineX = 2;
      } else {
        lineX = this.x;
      }
    } else if (this.x > this.config.width - this.squareSize) {
      // near right edge
      leftRectWithText = this.config.width - 2 * this.squareSize;
      if (this.x > this.config.width) {
        lineX = this.config.width - 2;
      } else {
        lineX = this.x;
      }
    }
    this.line = new fabric.Line([lineX, 1, lineX, this.config.height], STROKE);
    this.line.id = 'line';
    this.canvas.add(this.line);
    const rect = new fabric.Rect({
      width: this.squareSize * 2,
      height: this.squareSize,
      stroke: '#EB6D17',
      fill: '#EB6D17',
      rx: 3,
      ry: 3,
      selectable: false,
      hasBorders: false,
      hoverCursor: 'arrow',
      originX: 'center',
      originY: 'center',
    });
    const text = new fabric.Text(this.text, {
      fontFamily: 'Lato, Arial, Helvetica, sans-serif',
      fontSize: 12,
      fontWeight: 700,
      originX: 'center',
      originY: 'center',
      fill: 'white',
      selectable: false,
      hoverCursor: 'arrow',
    });
    this.rectWithText = new fabric.Group([rect, text], {
      left: leftRectWithText,
      top: 1,
      // `zIndex` isn't in @types/fabric's IGroupOptions but fabric.Group accepts it at runtime
      ...({ zIndex: 20 } as any),
      selectable: false,
      hoverCursor: 'arrow',
    });
    this.rectWithText.id = 'rectWithText';
    this.canvas.add(this.rectWithText);
    this.canvas.bringToFront(this.rectWithText);
  };

  EventIndicatorImpl.prototype.render = function () {
    this.canvas.renderAll();
  };

  EventIndicatorImpl.prototype.update = function (sampleIndex: number, offsetX: number) {
    this.xr = sampleIndex;
    this.x = (this.xr - offsetX) / this.spp;
    if (this.line && this.rectWithText) {
      if (this.x < this.squareSize) {
        // near left edge
        this.rectWithText.set({ left: 0 });
        this.rectWithText.setCoords();
        if (this.x < 0) {
          this.line.set({ x1: 2, x2: 2 });
          this.line.setCoords();
        } else {
          this.line.set({ x1: this.x, x2: this.x });
          this.line.setCoords();
        }
      } else if (this.x > this.config.width - this.squareSize) {
        // near right edge
        this.rectWithText.set({ left: this.config.width - 2 * this.squareSize });
        this.rectWithText.setCoords();
        if (this.x > this.config.width) {
          this.line.set({ x1: this.config.width - 2, x2: this.config.width - 2 });
          this.line.setCoords();
        } else {
          this.line.set({ x1: this.x, x2: this.x });
          this.line.setCoords();
        }
      } else {
        this.line.set({ x1: this.x, x2: this.x });
        this.line.setCoords();
        this.rectWithText.set({ left: this.x - this.squareSize });
        this.rectWithText.setCoords();
      }
      this.canvas.bringToFront(this.rectWithText);
    }
  };

  EventIndicatorImpl.prototype.updateConfig = function () {};

  EventIndicatorImpl.prototype.scroll = function (offsetX: number) {
    this.x = (this.xr - offsetX) / this.spp;
    if (this.line && this.rectWithText) {
      if (this.x < this.squareSize) {
        // near left edge
        this.rectWithText.set({ left: 0 });
        this.rectWithText.setCoords();
        if (this.x < 0) {
          this.line.set({ x1: 2, x2: 2 });
          this.line.setCoords();
        } else {
          this.line.set({ x1: this.x, x2: this.x });
          this.line.setCoords();
        }
      } else if (this.x > this.config.width - this.squareSize) {
        // near right edge
        this.rectWithText.set({ left: this.config.width - 2 * this.squareSize });
        this.rectWithText.setCoords();
        if (this.x > this.config.width) {
          this.line.set({ x1: this.config.width - 2, x2: this.config.width - 2 });
          this.line.setCoords();
        } else {
          this.line.set({ x1: this.x, x2: this.x });
          this.line.setCoords();
        }
      } else {
        this.line.set({ x1: this.x, x2: this.x });
        this.line.setCoords();
        this.rectWithText.set({ left: this.x - this.squareSize });
        this.rectWithText.setCoords();
      }
      this.canvas.bringToFront(this.rectWithText);
    }
  };

  EventIndicatorImpl.prototype.isSelected = function (pointer: any) {
    return false; // Can not select this annotation
  };

  EventIndicatorImpl.prototype.select = function () {
    // Can not select this annotation
  };

  EventIndicatorImpl.prototype.deselect = function () {
    // Can not deselect this annotation
  };

  EventIndicatorImpl.prototype.isVisibility = function (isVisibility: boolean) {
    this.line.visible = isVisibility;
    this.rectWithText.visible = isVisibility;
  };

  EventIndicatorImpl.prototype.getJSONObject = function () {
    return {
      type: this.type,
      position: this.xr,
    };
  };

  EventIndicatorImpl.prototype.import = function (data: any) {
    // This object import by create instance
  };
})();

const EventIndicator: EventIndicatorCtor = EventIndicatorImpl;

export { EventIndicator };
export default { EventIndicator };
